/**
 * Reusable stats widget component
 * Displays a metric with label, value, and optional trend indicator
 */

import { Link } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  ArrowRight,
  LucideIcon,
} from 'lucide-react';

export interface StatItem {
  id: string;
  label: string;
  value: string | number;
  icon?: LucideIcon;
  trend?: {
    value: number;
    label?: string;
  };
  linkUrl?: string;
  linkText?: string;
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'error';
}

interface StatsWidgetProps {
  stats: StatItem[];
  columns?: 2 | 3 | 4;
  className?: string;
  loading?: boolean;
}

const variantStyles = {
  default: {
    bg: 'bg-gray-50 dark:bg-gray-700/50',
    icon: 'text-gray-600 dark:text-gray-400',
    value: 'text-gray-900 dark:text-gray-100',
  },
  primary: {
    bg: 'bg-primary-50 dark:bg-primary-900/30',
    icon: 'text-primary-600 dark:text-primary-400',
    value: 'text-primary-700 dark:text-primary-300',
  },
  success: {
    bg: 'bg-green-50 dark:bg-green-900/30',
    icon: 'text-green-600 dark:text-green-400',
    value: 'text-green-700 dark:text-green-300',
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-900/30',
    icon: 'text-amber-600 dark:text-amber-400',
    value: 'text-amber-700 dark:text-amber-300',
  },
  error: {
    bg: 'bg-red-50 dark:bg-red-900/30',
    icon: 'text-red-600 dark:text-red-400',
    value: 'text-red-700 dark:text-red-300',
  },
};

function StatCard({ stat, loading }: { stat: StatItem; loading?: boolean }): JSX.Element {
  const variant = stat.variant || 'default';
  const styles = variantStyles[variant];
  const Icon = stat.icon;

  const content = (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-shadow h-full">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1 truncate">
            {stat.label}
          </p>
          {loading ? (
            <div className="h-8 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          ) : (
            <p className={`text-2xl font-bold ${styles.value}`}>
              {stat.value}
            </p>
          )}

          {/* Trend indicator */}
          {stat.trend && !loading && (
            <div className="flex items-center mt-2">
              {stat.trend.value >= 0 ? (
                <TrendingUp size={14} className="text-green-500 mr-1" />
              ) : (
                <TrendingDown size={14} className="text-red-500 mr-1" />
              )}
              <span className={`text-xs font-medium ${stat.trend.value >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {stat.trend.value >= 0 ? '+' : ''}{stat.trend.value}%
              </span>
              {stat.trend.label && (
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                  {stat.trend.label}
                </span>
              )}
            </div>
          )}

          {/* Link */}
          {stat.linkUrl && stat.linkText && !loading && (
            <div className="mt-3">
              <span className="inline-flex items-center text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">
                {stat.linkText}
                <ArrowRight size={14} className="ml-1" />
              </span>
            </div>
          )}
        </div>

        {/* Icon */}
        {Icon && (
          <div className={`p-2.5 ${styles.bg} rounded-lg ml-3 flex-shrink-0`}>
            <Icon size={20} className={styles.icon} />
          </div>
        )}
      </div>
    </div>
  );

  if (stat.linkUrl) {
    return (
      <Link to={stat.linkUrl} className="block">
        {content}
      </Link>
    );
  }

  return content;
}

export function StatsWidget({
  stats,
  columns = 4,
  className = '',
  loading = false,
}: StatsWidgetProps): JSX.Element {
  if (stats.length === 0) {
    return <></>;
  }

  const gridCols = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'sm:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div className={`grid grid-cols-1 ${gridCols[columns]} gap-4 ${className}`}>
      {stats.map(stat => (
        <StatCard key={stat.id} stat={stat} loading={loading} />
      ))}
    </div>
  );
}

export default StatsWidget;
